/** `${null}.pdf` / `${undefined}.pdf` from callers with missing names */
const BAD_DOWNLOAD_NAMES = new Set(['null.pdf', 'undefined.pdf', '.pdf']);

const isIOSUserAgent = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

const safeDownloadName = (filename: string) => {
  const trimmed = filename?.trim() ?? '';
  return trimmed && !BAD_DOWNLOAD_NAMES.has(trimmed.toLowerCase()) ? trimmed : 'download.pdf';
};

/**
 * True when running inside the Nile iOS app WKWebView with a native download bridge,
 * which reports the outcome through a downloadComplete/downloadError window message.
 */
export const hasIOSNativeDownloadHandler = (): boolean =>
  Boolean(window.webkit?.messageHandlers?.startDownload || window.webkit?.messageHandlers?.saveDownload);

const anchorDownload = (blob: Blob, safeFilename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = safeFilename;
  link.click();
};

const blobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Saves a PDF the page fetched itself: handed to the iOS app's bridge when present
 * (which then reports via window message), else an anchor download.
 */
export const saveBlob = async (blob: Blob, filename: string) => {
  const safeFilename = safeDownloadName(filename);
  const handlers = isIOSUserAgent() ? window.webkit?.messageHandlers : undefined;

  if (handlers?.saveDownload) {
    handlers.saveDownload.postMessage({ data: await blobToBase64(blob), filename: safeFilename });
    return;
  }
  // startDownload fetches without our auth headers, so it gets the bytes as a data: URL.
  if (handlers?.startDownload) {
    handlers.startDownload.postMessage({
      url: `data:application/pdf;base64,${await blobToBase64(blob)}`,
      filename: safeFilename,
    });
    return;
  }

  anchorDownload(blob, safeFilename);
};

export const downloadFile = async (url: string, filename: string) => {
  const safeFilename = safeDownloadName(filename);

  // Check for iOS WebView startDownload handler
  const startDownloadHandler = window.webkit?.messageHandlers?.startDownload;

  if (isIOSUserAgent() && startDownloadHandler) {
    // Send message to Swift to start downloading
    startDownloadHandler.postMessage({
      url,
      filename: safeFilename,
    });

    // Return immediately - downloading status will be communicated via window.postMessage
    return;
  }

  // Fallback to standard blob URL download for non-iOS or when handler is not available
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  anchorDownload(await response.blob(), safeFilename);
};
